import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DeviceHubOutlinedIcon from '@mui/icons-material/DeviceHubOutlined'
import { useBetaMode } from '@hooks/useBetaMode'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { loginSuccess } from '@store/slices/authSlice'
import { useChangePasswordMutation } from '@store/api/authApi'
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from '@store/api/notificationApi'
import { useGetCurrentSubscriptionQuery } from '@store/api/subscriptionApi'
import PageHeader from '@components/layout/PageHeader'

const MAX_BACKUP_BYTES = 5 * 1024 * 1024

const Settings: React.FC = () => {
  const dispatch = useAppDispatch()
  const { beta } = useBetaMode()
  const { token, user } = useAppSelector((state) => state.auth)
  const { data: notificationPrefs } = useGetNotificationPreferencesQuery()
  const { data: currentSubscription } = useGetCurrentSubscriptionQuery()
  const [updateNotificationPreferences, { isLoading: isSavingNotifications }] =
    useUpdateNotificationPreferencesMutation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [dataMessage, setDataMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [changePassword, { isLoading: isChangingPassword }] = useChangePasswordMutation()

  const downloadAuthenticated = (url: string, filename: string) => {
    fetch(url, { headers: { Authorization: `Bearer ${token ?? ''}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = filename
        link.click()
        URL.revokeObjectURL(objectUrl)
      })
      .catch(() => {
        alert('Download failed. Please try again.')
      })
  }

  const handleExport = () => {
    downloadAuthenticated('/api/v1/export/collection?format=csv', 'cigar-collection.csv')
  }

  const handleBackup = () => {
    downloadAuthenticated('/api/v1/backup', 'cigartrack-backup.json')
  }

  const handleRestoreBackup = async (file: File) => {
    setDataMessage(null)
    if (file.size > MAX_BACKUP_BYTES) {
      setDataMessage({ type: 'error', text: 'Backup file is too large. Maximum is 5 MB.' })
      return
    }

    try {
      const backup = JSON.parse(await file.text())
      const response = await fetch('/api/v1/backup/restore', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backup }),
      })

      const body = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(body?.message || 'Restore failed')
      }

      setDataMessage({
        type: 'success',
        text: `Backup restored. Imported ${body?.data?.result?.imported ?? 0}, merged ${body?.data?.result?.merged ?? 0}.`,
      })
    } catch (error) {
      setDataMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Restore failed. Check the backup file and try again.',
      })
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value })
    if (passwordError) setPasswordError('')
    if (passwordSuccess) setPasswordSuccess('')
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordError('New password and confirmation do not match')
      return
    }

    try {
      const result = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      }).unwrap()
      if (user && result?.accessToken) {
        dispatch(loginSuccess({ user, token: result.accessToken }))
      }
      setPasswordSuccess('Password changed successfully')
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' })
    } catch (err: unknown) {
      const apiError = err as { data?: { message?: string }; message?: string }
      setPasswordError(
        apiError?.data?.message || apiError?.message || 'Failed to change password. Please try again.'
      )
    }
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <PageHeader title="Settings" />

      {/* Account section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LockOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Account
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update your account password. Your new password must be at least 8 characters and include uppercase, lowercase, and a number.
          </Typography>

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              disabled={isChangingPassword}
            />
            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              disabled={isChangingPassword}
              helperText="At least 8 characters with uppercase, lowercase, and a number"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmNewPassword"
              type="password"
              value={passwordForm.confirmNewPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              disabled={isChangingPassword}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isChangingPassword}
              sx={{ mt: 2 }}
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CreditCardOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Subscription
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {beta
              ? 'You are on the free plan during beta.'
              : (
                <>
                  Current plan:{' '}
                  <strong>{currentSubscription?.displayName ?? 'Loading…'}</strong>
                  {currentSubscription?.plan ? ` (${currentSubscription.plan})` : ''}
                </>
              )}
          </Typography>
          {!beta && (
            <Button component={RouterLink} to="/app/settings/subscription" variant="outlined">
              Manage Subscription
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Active sessions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DeviceHubOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Active Sessions
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            View and revoke sign-in sessions on other devices.
          </Typography>
          <Button component={RouterLink} to="/app/settings/sessions" variant="outlined">
            Manage Sessions
          </Button>
        </CardContent>
      </Card>

      {/* Preferences section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <NotificationsOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Preferences
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={notificationPrefs?.emailNotifications ?? true}
                disabled={isSavingNotifications || !notificationPrefs}
                onChange={() => {
                  if (!notificationPrefs) return
                  void updateNotificationPreferences({
                    emailNotifications: !notificationPrefs.emailNotifications,
                  })
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Email Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive updates and alerts via email (saved to your account)
                </Typography>
              </Box>
            }
            sx={{ width: '100%', ml: 0, justifyContent: 'space-between', flexDirection: 'row-reverse' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            <RouterLink to="/app/settings/notifications">More notification options</RouterLink>
          </Typography>
        </CardContent>
      </Card>

      {/* Data import / export */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DownloadOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Data Import & Export
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {dataMessage && (
            <Alert severity={dataMessage.type} sx={{ mb: 2 }}>
              {dataMessage.text}
            </Alert>
          )}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Import Collection (CSV)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload a CSV with brand, cigar line, and vitola columns
                </Typography>
              </Box>
              <Button component={RouterLink} to="/app/import" variant="outlined" size="small">
                Import CSV
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Export Collection (CSV)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Download your collection for spreadsheets or re-import
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleExport}
              >
                Export CSV
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Backup (JSON)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Full collection snapshot for disaster recovery
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={handleBackup}>
                Download Backup
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Restore Backup (JSON)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Restore humidors and collection cigars from a CigarTrack backup
                </Typography>
              </Box>
              <Button component="label" variant="outlined" size="small">
                Restore Backup
                <input
                  hidden
                  type="file"
                  accept="application/json,.json"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) void handleRestoreBackup(file)
                    event.target.value = ''
                  }}
                />
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Danger Zone section */}
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberOutlinedIcon color="error" />
            <Typography variant="subtitle1" fontWeight={600} color="error">
              Danger Zone
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Delete Account
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently remove your account and all associated data
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Account
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Account confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Account deletion requires manual review. Please contact support to delete your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Settings
